import { defineStore } from "pinia";
import type { LocationQuery } from "vue-router";
import LxcError from "~/core/models/LxcError.class";
import { Filters } from "~/core/models/filters";
import type { Sector } from "~/modules/sector/models/Sector.interface";
import sectorsService from "~/modules/sector/services/sectors.service";
import { getFlattenSectors } from "~/modules/sector/utils/getFlattenSectors";
import { getGrantedSectors } from "~/modules/sector/utils/getGrantedSectors";
import { UserSessionEntityType } from "~/modules/user/models/UserSessionEntityType.enum";
import { useUserSession } from "~/modules/user/stores/useUserSession";

export const useSectorStore = defineStore("sectorStore", () => {
  const sectors = ref<Sector | undefined>();
  const allSectors = ref<Sector[] | undefined>();
  const selectedSectors = ref<Sector[]>([]);
  const availableSectors = ref<Sector[]>([]);
  const selectedSectorCodes = ref<number[]>([]);
  let isInitialized = false;

  function getSectorCodes(sectors: Sector[]): number[] {
    return sectors.map((sector) => sector.code);
  }

  function getSectorName(sectorId?: number): string | undefined {
    const sector: Sector | undefined = availableSectors.value.find(
      (currentSector) => {
        return currentSector.code === sectorId;
      },
    );
    return sector?.name;
  }

  async function retrieveSectors() {
    const { userSession } = useUserSession();
    if (userSession?.entity.code) {
      sectors.value = await sectorsService.getSectors({
        organizationCode: userSession.entity.code,
      });

      if (LxcError.check(sectors.value)) {
        throw sectors.value.toError();
      } else {
        allSectors.value = getFlattenSectors(sectors.value);
        availableSectors.value = getGrantedSectors([sectors.value]);
      }
    } else {
      throw new Error("Organization code is undefined");
    }
  }

  function changeSelection(newSelection: Sector[]) {
    selectedSectors.value = newSelection;
    selectedSectorCodes.value = getSectorCodes(newSelection);
  }

  async function resetSectorsSelection() {
    selectedSectors.value = [];
    selectedSectorCodes.value = [];
  }

  /**
   * Initialize the store using the parameters in the URL.
   * This method can be called multiple times, but the initialization will only be done the first time.
   *
   * @param query query to use to initialize the store
   */
  async function init(query: LocationQuery) {
    const { userSession } = useUserSession();
    if (
      !isInitialized &&
      userSession?.entity?.type === UserSessionEntityType.OPERATOR
    ) {
      await retrieveSectors();

      const sectors = query[Filters.SECTORS + "[]"] as string[];

      if (sectors !== undefined) {
        const selectedSectors = availableSectors.value.filter(
          (sector: Sector) => sectors.includes(sector.code.toString()),
        );
        changeSelection(selectedSectors);
      } else {
        selectedSectors.value = availableSectors.value;
      }

      isInitialized = true;
    }
  }

  return {
    init,
    sectors,
    allSectors,
    selectedSectors,
    selectedSectorCodes,
    availableSectors,
    retrieveSectors,
    changeSelection,
    resetSectorsSelection,
    getSectorName,
  };
});
